/**
 * The reason gate (concept §11, D7 — 2026-08-21).
 *
 * There is no investigator role and no identity store in NOVA. Who may open a
 * per-user view is a backend allow-list of sign-in mails
 * (`NOVA_FO_INVESTIGATORS`, 403 otherwise — every pilot user of the browser
 * extension is a signed-in NOVA user, so the sign-in alone cannot be the
 * gate). Past that, **every per-user call carries a reason** — a ticket or
 * report id, or free text of at least four characters — which the backend
 * audit-logs together with the viewer and the subject. The UI asks once per browser session (`sessionStorage`, key
 * `nova.fo.reason`), shows the active reason beside the page title so it is
 * never invisible, and lets the investigator change it mid-session — a second
 * ticket is a second reason.
 *
 * What the gate is not: a security boundary. The backend refuses a call
 * without a reason (400) regardless of what the UI does; this component
 * exists so the investigator states the purpose *before* the first identity
 * is fetched, not after.
 */

import { Icon, KeyRound, Pencil } from "@norican/icons";
import { Button, Dialog, Field, Input, Typography } from "@norican/react";
import { type FormEvent, useCallback, useId, useState } from "react";

import { logger } from "@/lib/logger";

import {
  isReasonValid,
  normaliseReason,
  readStoredReason,
  REASON_MIN_LENGTH,
  writeStoredReason,
} from "./console-format";

function sessionStorageOrNull(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export interface InvestigationReason {
  /** The active, valid reason — or `null`, in which case no per-user query fires. */
  reason: string | null;
  /** Set (and persist) a reason. Returns false and changes nothing if it is too short. */
  setReason: (reason: string) => boolean;
  /** Forget the reason for this session. */
  clearReason: () => void;
}

/** The session's investigation reason, read once from storage and written through. */
export function useInvestigationReason(): InvestigationReason {
  const [reason, setReasonState] = useState<string | null>(() =>
    readStoredReason(sessionStorageOrNull()),
  );

  const setReason = useCallback((next: string) => {
    if (!isReasonValid(next)) {
      return false;
    }
    const stored = writeStoredReason(sessionStorageOrNull(), next);
    setReasonState(stored);
    logger.debug("[fo-console] investigation reason set");
    return stored != null;
  }, []);

  const clearReason = useCallback(() => {
    writeStoredReason(sessionStorageOrNull(), null);
    setReasonState(null);
    logger.debug("[fo-console] investigation reason cleared");
  }, []);

  return { reason, setReason, clearReason };
}

interface ReasonDialogProps {
  open: boolean;
  /** Close without a reason — the page stays locked. */
  onCancel: () => void;
  /** Called with the normalised reason once it validates. */
  onSubmit: (reason: string) => void;
  /** Pre-fill when editing an existing reason. */
  initialReason?: string | null;
}

/** The modal that asks for the reason. Enter submits; Esc cancels. */
export function ReasonDialog({ open, onCancel, onSubmit, initialReason }: ReasonDialogProps) {
  const [draft, setDraft] = useState(initialReason ?? "");
  const [touched, setTouched] = useState(false);
  const descriptionId = useId();
  const valid = isReasonValid(draft);
  const showError = touched && !valid;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (valid) {
      onSubmit(normaliseReason(draft));
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup size="md" aria-describedby={descriptionId}>
          <form onSubmit={submit} className="contents">
            <Dialog.Header>
              <Dialog.Title>
                <span className="inline-flex items-center gap-2">
                  <Icon icon={KeyRound} size="sm" aria-hidden="true" />
                  Why are you looking at this person?
                </span>
              </Dialog.Title>
              <Dialog.CloseButton />
            </Dialog.Header>
            <Dialog.Body>
              <div className="flex flex-col gap-4">
                <Dialog.Description id={descriptionId}>
                  Per-user views are computed on demand and every one is audit-logged with your
                  name, the person you looked at, and this reason. A ticket or report id is best;
                  free text of at least {REASON_MIN_LENGTH} characters is accepted. It is kept for
                  this browser session only.
                </Dialog.Description>
                <Field
                  label="Reason"
                  required
                  hint="e.g. INC0012345, the Hub report id, or “Helle asked about FR quotes”"
                  error={
                    showError
                      ? `At least ${REASON_MIN_LENGTH} characters — the backend refuses anything shorter.`
                      : undefined
                  }
                >
                  <Input
                    // The dialog exists to collect this one value, so the field
                    // takes focus when it opens.
                    autoFocus
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    onBlur={() => setTouched(true)}
                    placeholder="Ticket, report id, or a short reason"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </Field>
              </div>
            </Dialog.Body>
            <Dialog.Footer>
              <div className="flex w-full flex-wrap justify-end gap-2">
                <Button type="button" variant="ghost" tone="neutral" onClick={onCancel}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!valid}>
                  Continue
                </Button>
              </div>
            </Dialog.Footer>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface ReasonControlProps {
  reason: string | null;
  onChange: () => void;
}

/** The small "reason: … (change)" readout that keeps the active reason in view. */
export function ReasonControl({ reason, onChange }: ReasonControlProps) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Typography variant="body-sm" tone="muted" as="span">
        reason:{" "}
        {reason ? (
          <span className="text-fg font-medium" title={reason}>
            {reason}
          </span>
        ) : (
          <span className="text-warning-fg font-medium">none — per-user views are locked</span>
        )}
      </Typography>
      <Button
        variant="ghost"
        tone="neutral"
        size="sm"
        startIcon={<Icon icon={Pencil} size="xs" />}
        onClick={onChange}
      >
        {reason ? "change" : "enter a reason"}
      </Button>
    </span>
  );
}
